import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, map, tap } from 'rxjs';
import {
  LoyaltyCodesForProductService,
  LoyaltyFavoriteProductService,
  LoyaltyProductsWithCount,
  LoyaltySearchService,
  LoyaltyService,
  ViewLoyaltyProduct,
} from '@src/api';
import { isEqual } from 'lodash-es';

import { LoyaltyProgramFilterData, MapFilterType, SelectCity } from '../types';
import { LoyaltyProgramCategoryList, LoyaltyProgramListProduct } from '../models';
import { apiCategoriesToCategoriesUI } from '../utils/apiCategoriesToCategoriesUI';

import { DEFAULT_ITEMS_WITH_PAGING, ITEMS_PER_PAGE_BY_DEFAULT } from './constants';
import { convertToProduct } from './utils/convertToProduct';
import { convertToProductList } from './utils/convertToProductList';
import { LoyaltyProgramProductWithPagingResult } from './types';
import {
  LpFavoriteProductsStoreService,
  LpNewProductsStoreService,
  LpSimilarProductsStoreService,
  LpMyProductCodesStoreService,
  ProductsStoreKeys,
  ProductCodesStoreKeys,
} from './store';

@Injectable({
  providedIn: 'root',
})
export class LoyaltyProgramReadService {
  private readonly loyaltySearchService = inject(LoyaltySearchService);
  private readonly api = inject(LoyaltyService);
  private readonly loyaltyFavoriteProductService = inject(LoyaltyFavoriteProductService);
  private readonly loyaltyCodesForProductService = inject(LoyaltyCodesForProductService);
  private readonly lpFavoriteProductsStore = inject(LpFavoriteProductsStoreService);
  private readonly lpNewProductsStore = inject(LpNewProductsStoreService);
  private readonly lpSimilarProductsStore = inject(LpSimilarProductsStoreService);
  private readonly lpMyProductCodesStore = inject(LpMyProductCodesStoreService);

  readonly products$ = new BehaviorSubject<LoyaltyProgramProductWithPagingResult>({ ...DEFAULT_ITEMS_WITH_PAGING });
  private productsByFilterStart = 0;
  private productsByFilterHasMore = true;
  private filtersState = '';

  readonly ownProducts$ = new BehaviorSubject<LoyaltyProgramProductWithPagingResult>({ ...DEFAULT_ITEMS_WITH_PAGING });
  private ownProductsStart = 0;
  private ownProductsHasMore = false;

  readonly mapProducts$ = new BehaviorSubject<LoyaltyProgramListProduct[] | undefined>(undefined);

  readonly productsFromFavoriteCategories$ = new BehaviorSubject<LoyaltyProgramCategoryList[] | null>(null);
  private favoriteCategoriesState = '';

  getProductsByFilter(filter: LoyaltyProgramFilterData, city?: SelectCity, query?: string, nextPage = false) {
    if (nextPage && !this.productsByFilterHasMore) {
      return;
    }

    if (!this.checkFiltersChanges(filter, city, query, nextPage)) {
      return;
    }

    const { categories = [] } = filter ?? {};

    if (nextPage) {
      this.productsByFilterStart += ITEMS_PER_PAGE_BY_DEFAULT;
    } else {
      this.productsByFilterStart = 0;
      this.productsByFilterHasMore = false;
    }

    this.api
      .searchForLoyaltyProduct({
        name: query?.trim() ? query?.trim() : undefined,
        categoryIds: categories?.length ? categories.map(item => item.id) : undefined,
        cityId: city?.cityId,
        start: this.productsByFilterStart,
        top: ITEMS_PER_PAGE_BY_DEFAULT,
      })
      .subscribe(result => {
        this.productsByFilterHasMore = this.preparePagingResult(this.products$, nextPage, result);
      });
  }

  getOwnProducts(nextPage = false) {
    if (nextPage && !this.ownProductsHasMore) {
      return;
    }

    if (nextPage) {
      this.ownProductsStart += ITEMS_PER_PAGE_BY_DEFAULT;
    } else {
      this.ownProductsStart = 0;
      this.ownProductsHasMore = false;
    }

    this.api.myProducts(this.ownProductsStart, ITEMS_PER_PAGE_BY_DEFAULT).subscribe(result => {
      this.ownProductsHasMore = this.preparePagingResult(this.ownProducts$, nextPage, result);
    });
  }

  getFavoriteProducts() {
    this.loyaltyFavoriteProductService
      .apiLoyaltyProductFavoritesListGet()
      .pipe(
        tap(result => {
          const products = result.map(item => convertToProduct(item));
          this.lpFavoriteProductsStore.setData(ProductsStoreKeys.Favorite, products);
        }),
      )
      .subscribe();
  }

  getMyProductCodes() {
    this.loyaltyCodesForProductService
      .myProductCodes()
      .pipe(
        tap(codes => {
          this.lpMyProductCodesStore.setData(ProductCodesStoreKeys.All, codes);
        }),
      )
      .subscribe();
  }

  getSimilarProducts(productId: string, unionId?: string, cityId?: number) {
    this.api
      .similarProducts(productId, unionId, cityId)
      .pipe(
        map(result => {
          return (result as LoyaltyProductsWithCount).items?.map(item => {
            return convertToProduct(item);
          });
        }),
        tap(products => {
          this.lpSimilarProductsStore.setData(productId, products ?? []);
        }),
      )
      .subscribe();
  }

  getMapProducts(filter: MapFilterType) {
    const { city, query, categories, bounds } = filter;

    this.loyaltySearchService
      .getLoyaltyProductsOnMap({
        cityId: city?.cityId,
        nameTemplate: query?.trim() ? query?.trim() : undefined,
        categoryIds: categories?.length ? categories.map(item => item.id) : undefined,
        lowerLeftCorner: bounds.lowerLeftCorner,
        upperRightCorner: bounds.upperRightCorner,
      })
      .pipe(
        map(items => {
          return items?.map(item => {
            return convertToProductList(item);
          });
        }),
      )
      .subscribe(result => {
        if (!isEqual(this.mapProducts$.value, result)) {
          this.mapProducts$.next(result);
        }
      });
  }

  getNewProducts(cityId?: number) {
    if (!cityId) return;

    this.loyaltySearchService
      .getNewLoyaltyProducts({ cityId })
      .pipe(
        map(items => {
          return items?.map(item => {
            return convertToProductList(item);
          });
        }),
        tap(products => {
          this.lpNewProductsStore.setData(cityId.toString(), products);
        }),
      )
      .subscribe();
  }

  getProductsFromFavoriteCategories(cityId?: number, categories?: string[]) {
    if (!this.checkFavoriteCategoriesChanges(cityId, categories)) {
      return;
    }

    if (!categories?.length) {
      this.productsFromFavoriteCategories$.next(null);
      return;
    }

    this.loyaltySearchService
      .getProductsFromFavoriteCategories(cityId)
      .pipe(
        map(categories => {
          return apiCategoriesToCategoriesUI(categories);
        }),
      )
      .subscribe(result => {
        this.productsFromFavoriteCategories$.next(result);
      });
  }

  private preparePagingResult(
    stream$: BehaviorSubject<LoyaltyProgramProductWithPagingResult>,
    nextPage: boolean,
    next: ViewLoyaltyProduct[],
  ): boolean {
    const items = next.map(item => convertToProductList(item));
    const hasMore = items.length === ITEMS_PER_PAGE_BY_DEFAULT;

    if (nextPage) {
      // TODO: неясно как правильно организовать получение предыдущего значения
      stream$.next({ items: [...(stream$.value.items ?? []), ...items], hasMore });
    } else {
      stream$.next({ items, hasMore });
    }

    return hasMore;
  }

  get isOwnProductsHasMore() {
    return this.ownProductsHasMore;
  }

  get isProductsByFilterHasMore() {
    return this.productsByFilterHasMore;
  }

  private productsReset() {
    this.products$.next({ items: null, hasMore: false });
  }

  private setFiltersState(filter?: LoyaltyProgramFilterData, city?: SelectCity, query?: string) {
    this.filtersState = this.getStateByFilters(filter, city, query);
  }

  private getStateByFilters(filter?: LoyaltyProgramFilterData, city?: SelectCity, query?: string) {
    return JSON.stringify({
      filter,
      cityId: city?.cityId,
      query,
    });
  }

  private checkFiltersChanges(
    filter?: LoyaltyProgramFilterData,
    city?: SelectCity,
    query?: string,
    nextPage?: boolean,
  ): boolean {
    const filterChanged = this.filtersState !== this.getStateByFilters(filter, city, query);

    if (filterChanged || (nextPage && this.productsByFilterHasMore)) {
      this.setFiltersState(filter, city, query);

      if (filterChanged) {
        this.productsReset();
      }

      return true;
    }

    return false;
  }

  private productsFromFavoriteCategoriesReset() {
    this.productsFromFavoriteCategories$.next(null);
  }

  private setFavoriteCategoriesState(cityId?: number, categories?: string[]) {
    this.favoriteCategoriesState = this.getStateByFavoriteCategories(cityId, categories);
  }

  private getStateByFavoriteCategories(cityId?: number, categories?: string[]) {
    return JSON.stringify({
      cityId,
      categories,
    });
  }

  private checkFavoriteCategoriesChanges(cityId?: number, categories?: string[]): boolean {
    const favoriteCategoriesChanged =
      this.favoriteCategoriesState !== this.getStateByFavoriteCategories(cityId, categories);

    if (favoriteCategoriesChanged) {
      this.setFavoriteCategoriesState(cityId, categories);

      if (favoriteCategoriesChanged) {
        this.productsFromFavoriteCategoriesReset();
      }

      return true;
    }

    return false;
  }
}
